import { Routes, Route } from 'react-router-dom';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import { useIsAuthenticated } from './hooks';
import {
  AccountDetails,
  AccountStatement,
  AccountSummary,
  ActivityLog,
  ActivityLogs,
  AffiliateListing,
  AffiliateReq,
  AllDownlineListing,
  BankerDashboard,
  BankerDetail,
  Bankers,
  BannerManagement,
  BetList,
  BetSetting,
  BussinessReports,
  CasinoBetList,
  CasinoControl,
  CasinoDetail,
  CasinoPlByDownline,
  CasinoPlByPlayer,
  CategoryControl,
  CmsManagement,
  ComissionReport,
  AffiliateActiveUserOfWeek,
  ContentManagement,
  DashBoard,
  DashboardPage,
  DWRequest,
  EventManagement,
  Export,
  FilteredGames,
  GameControl,
  InactiveMemerList,
  Login,
  ManageAccount,
  NotFoundPage,
  NotificationManagement,
  OnboardingPage,
  PaymentGatewayManager,
  PaymentGatewayTransactions,
  PlByEvent,
  PlByPlayer,
  PlDownline,
  ProviderControl,
  Risk,
  Setting,
  SubUsers,
  SubUsersListing,
  SupportContactManagement,
  TransactionCode,
  TransactionHistory,
  TwoFactorAuthentication,
  UserAccountStatement,
  UserBetHistory,
  UserBettingPL,
  UserIpTracking,
  RoleWiseAccountStatement,
  CryptoConversion,
  BonusStatement,
  PolicyManagement,
  RiskDetailsPage,
  Settlement,
} from '@/components/routes';
import AffiliateSetting from './pages/AffiliateManagement/AffiliateSetting';
import { useRoleCheck } from './hooks/useRoleCheck';
import ProtectedAdminRoutes from './components/routes/ProtectedAdminRoutes';
import ModuleRoute from './components/ModuleRoute';
import RolesAndPrivileges from './pages/RolesAndPriviledges';
import Roles from './pages/RolesAndPriviledges/Roles';
import AccessControl from './pages/RolesAndPriviledges/AccessControl';
import CreateRoles from './pages/RolesAndPriviledges/components/CreateRoles';
import { PermissionProvider } from './context/PermissionContext';
import { useGetUserRolesDetailsQuery } from './store/api/rolesApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { Permission } from './types/rolesTypes';

export function App() {
  const isAuth = useIsAuthenticated();

  const user = useSelector((state: RootState) => state.user);
  const userId = user?.data?.id;
  const userRole = user?.data?.role?.name;

  const { data, isLoading } = useGetUserRolesDetailsQuery(userId ? Number(userId) : skipToken, {
    skip: !userId,
  });
  const userData = data?.data?.[0];

  const { isOwner } = useRoleCheck();
  const ownerOnly = (element: React.ReactNode) => (
    <ProtectedAdminRoutes allowed={isOwner}>{element}</ProtectedAdminRoutes>
  );
  return (
    <PermissionProvider user={userData as Permission} isLoading={isLoading}>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          {/* <Route path="/register" element={<RegisterPage />} /> */}
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            {isAuth && userRole !== 'BANKER' ? (
              <>
                <Route
                  path="/"
                  element={
                    <ModuleRoute moduleName="Dashboard">
                      <DashboardPage />
                    </ModuleRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ModuleRoute moduleName="Dashboard">
                      <DashboardPage />
                    </ModuleRoute>
                  }
                />
              </>
            ) : (
              <>
                <Route path="/" element={<BankerDashboard />} />
                <Route path="/dashboard" element={<BankerDashboard />} />
              </>
            )}
            {/* <Route path="/profile" element={<ProfilePage />} /> */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            {/* Downline Routes */}
            <Route
              path="/members/:id"
              element={
                <ModuleRoute moduleName="Users">
                  <AllDownlineListing />
                </ModuleRoute>
              }
            />
            <Route path="/members/:id/:role/:user/:memberid/:upline" element={<SubUsers />} />
            <Route path="/settlement" element={<Settlement />} />
            <Route path="/members/:id/sub/:memberid/:upline/:userType" element={<AccountDetails />}>
              <Route index element={<AccountSummary />} />
              <Route path="account-summary" element={<AccountSummary />} />

              <Route path="account-statement" element={<UserAccountStatement />} />
              <Route path="bet-history" element={<UserBetHistory />} />
              <Route path="betting-pl" element={<UserBettingPL />} />

              <Route path="casino-detail" element={<CasinoDetail />} />

              <Route path="transaction-history" element={<TransactionHistory />} />
              <Route path="login-history" element={<ActivityLog />} />
            </Route>

            {/* Affiliate Management */}
            <Route path="/affiliate-dashboard" element={<DashBoard />} />
            <Route path="/affiliate-req" element={<AffiliateReq />} />
            <Route path="/affiliate-list" element={<AffiliateListing />} />
            <Route path="/affiliate-list/:id" element={<SubUsersListing />} />
            <Route path="/affiliate-comission-report" element={<ComissionReport />} />
            <Route path="/affiliate-comission-report/:id" element={<AffiliateActiveUserOfWeek />} />
            <Route path="/affiliate-setting" element={<AffiliateSetting />} />

            {/* Change Password Routes */}
            <Route
              path="/settings"
              element={
                // <ModuleRoute moduleName="Change Password">
                <Setting />
                // </ModuleRoute>
              }
            />
            {/* <Route path="/2fa" element={<TwoFactorAuthentication />} /> */}

            {/* Event Management*/}
            <Route element={<ModuleRoute moduleName="EventManagement" />}>
              <Route path="/event-management/:status?" element={<EventManagement />} />
              <Route path="/bet-setting" element={<BetSetting />} />
            </Route>
            {/* Casino management */}
            <Route
              path="/casino-management"
              element={
                <ModuleRoute moduleName="CasinoControl">
                  <CasinoControl />
                </ModuleRoute>
              }
            >
              <Route index element={<CategoryControl />} />
              <Route path="provider" element={<ProviderControl />} />
              <Route path="game" element={<GameControl />} />
            </Route>
            <Route
              path="/casino-management/games"
              element={
                <ModuleRoute moduleName="CasinoControl" requiredAccess={['read', 'detailedView']}>
                  <FilteredGames />
                </ModuleRoute>
              }
            />
            {/* Content Management */}
            <Route
              path="/content-management"
              element={
                <ModuleRoute moduleName="ContentManagement">
                  <ContentManagement />
                </ModuleRoute>
              }
            >
              <Route index element={<BannerManagement />} />
              <Route path="notification-management" element={<NotificationManagement />} />
              <Route path="support-management" element={<SupportContactManagement />} />
              <Route path="cms-management" element={<CmsManagement />} />
            </Route>
            {/* Bonus Management */}
            <Route path="/bonus">
              <Route
                path="policy-management"
                element={
                  <ModuleRoute moduleName="Bonus">
                    <PolicyManagement />
                  </ModuleRoute>
                }
              />
              <Route
                path="statement"
                element={
                  <ModuleRoute moduleName="Bonus">
                    <BonusStatement />
                  </ModuleRoute>
                }
              />
            </Route>
            {/* Banker Management */}
            <Route path="/bankers" element={ownerOnly(<Bankers />)} />
            <Route path="/bankers/:id/:details?/:mode?/:type?" element={<BankerDetail />} />
            <Route
              path="/requests/:mode?/:type?"
              element={
                <ModuleRoute moduleName="BankingManagement">
                  <DWRequest />
                </ModuleRoute>
              }
            />
            <Route
              path="/manage-account/:type?"
              element={
                <ModuleRoute moduleName="BankingManagement">
                  <ManageAccount />
                </ModuleRoute>
              }
            />
            <Route
              path="/payment-gateway"
              element={
                <ModuleRoute moduleName="BankingManagement">
                  <PaymentGatewayManager />
                </ModuleRoute>
              }
            />
            <Route
              path="/payment-gateway/transactions"
              element={
                <ModuleRoute moduleName="BankingManagement">
                  <PaymentGatewayTransactions />
                </ModuleRoute>
              }
            />
            <Route
              path="/crypto-conversion"
              element={
                <ModuleRoute moduleName="BankingManagement">
                  <CryptoConversion />
                </ModuleRoute>
              }
            />
            <Route path="/account-statement" element={<AccountStatement />} />
            <Route path="/banker-dashboard" element={<BankerDashboard />} />
            <Route
              path="/rolewise-account-statement"
              element={
                <ModuleRoute moduleName="Account Statement">
                  <RoleWiseAccountStatement />
                </ModuleRoute>
              }
            />

            {/* Risk Management Routes */}
            <Route
              path="/risk"
              element={
                <ModuleRoute moduleName="RiskManagement">
                  <Risk />
                </ModuleRoute>
              }
            />
            <Route path="/risk-details" element={<RiskDetailsPage />} />

            {/* Reports Routes */}
            <Route element={<ModuleRoute moduleName="MyReport" />}>
              <Route path="/bet-list" element={<BetList />} />
              <Route path="/casino-bet-list" element={<CasinoBetList />} />
              <Route path="/pl-by-event" element={<PlByEvent />} />
              <Route path="/pl-by-player" element={<PlByPlayer />} />
              <Route path="/pl-by-downline" element={<PlDownline />} />
              <Route path="/casinopl-by-player" element={<CasinoPlByPlayer />} />
              <Route path="/business-reports" element={<BussinessReports />} />
              <Route path="/casinopl-by-downline" element={<CasinoPlByDownline />} />
              <Route path="/inactive-members" element={<InactiveMemerList />} />
            </Route>
            {/* Roles And Privileges */}
            <Route element={<ModuleRoute moduleName="Roles-Privileges" />}>
              <Route path="/roles-and-privileges" element={<RolesAndPrivileges />}>
                <Route index element={<Roles />} />
                <Route path="access" element={<AccessControl />} />
              </Route>
              <Route
                path="create-roles/:id?/:type?"
                element={
                  <ModuleRoute moduleName="Roles-Privileges" requiredAccess={['read', 'create']}>
                    <CreateRoles />
                  </ModuleRoute>
                }
              />
            </Route>

            {/* activity logs */}
            <Route
              path="/activity-logs"
              element={
                <ModuleRoute moduleName="Activity Logs">
                  <ActivityLogs />
                </ModuleRoute>
              }
            />

            <Route
              path="/user-ips"
              element={
                <ModuleRoute moduleName="Activity Logs">
                  <UserIpTracking />
                </ModuleRoute>
              }
            />

            {/* Export History */}
            <Route
              path="/export"
              element={
                <ModuleRoute moduleName="Export History">
                  <Export />
                </ModuleRoute>
              }
            />
          </Route>
        </Route>
        {/* </Route> */}
        <Route path="/transaction-code" element={<TransactionCode />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </PermissionProvider>
  );
}
